"use client";

import { useEffect, type RefObject } from "react";

/** Observe existing media wrappers; adds no markup or layout differences. */
export function observePhotoImpressions(
  root: HTMLElement,
  onSeen: (id: string) => void,
) {
  if (typeof IntersectionObserver === "undefined") return () => {};
  const visible = new Set<HTMLElement>();
  const observed = new Set<HTMLElement>();
  const timers = new Map<HTMLElement, ReturnType<typeof setTimeout>>();
  const counted = new Set<string>();
  const cancel = (element: HTMLElement) => {
    clearTimeout(timers.get(element));
    timers.delete(element);
  };
  const start = (element: HTMLElement) => {
    const id = element.dataset.workPhotoId;
    if (
      !id ||
      counted.has(id) ||
      timers.has(element) ||
      document.visibilityState !== "visible"
    )
      return;
    timers.set(
      element,
      setTimeout(() => {
        timers.delete(element);
        if (
          !element.isConnected ||
          !visible.has(element) ||
          document.visibilityState !== "visible" ||
          counted.has(id)
        )
          return;
        counted.add(id);
        onSeen(id);
      }, 1000),
    );
  };
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const element = entry.target as HTMLElement;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          visible.add(element);
          start(element);
        } else {
          visible.delete(element);
          cancel(element);
        }
      }
    },
    { threshold: [0, 0.5] },
  );
  const sync = () => {
    for (const element of observed) {
      if (!root.contains(element)) {
        observer.unobserve(element);
        observed.delete(element);
        visible.delete(element);
        cancel(element);
      }
    }
    root
      .querySelectorAll<HTMLElement>("[data-work-photo-id]")
      .forEach((element) => {
        if (!observed.has(element)) {
          observed.add(element);
          observer.observe(element);
        }
      });
  };
  sync();
  // Carousels can insert clones and galleries append additional pages.
  const mutations = new MutationObserver(sync);
  mutations.observe(root, { childList: true, subtree: true });
  const onVisibility = () => {
    for (const element of visible) {
      cancel(element);
      if (document.visibilityState === "visible") start(element);
    }
  };
  document.addEventListener("visibilitychange", onVisibility);
  return () => {
    observer.disconnect();
    mutations.disconnect();
    timers.forEach(clearTimeout);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}

export function usePhotoImpressions(
  root: RefObject<HTMLElement | null>,
  onSeen: ((id: string) => void) | undefined,
  content: unknown,
) {
  useEffect(() => {
    if (root.current && onSeen)
      return observePhotoImpressions(root.current, onSeen);
  }, [root, onSeen, content]);
}
